import axios from 'axios';
import i18n from '../i18n.js';
import { attachUploadHandling } from './uploads.js';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_URL,
});

// Add a request interceptor to automatically add the Bearer token
api.interceptors.request.use(
  (config) => {
    let user = null;
    try { user = JSON.parse(localStorage.getItem('user')); } catch { user = null; }
    if (user && user.access_token) {
      config.headers.Authorization = `Bearer ${user.access_token}`;
    }
    // Tell the API which language to answer in. The backend translates its error messages on
    // the way out from this header (see the exception handler in app/main.py) — a header rather
    // than the stored preference, so it works for errors raised before anyone is signed in, a
    // failed login being the obvious one.
    try {
      const lng = i18n.resolvedLanguage || i18n.language;
      if (lng) config.headers['Accept-Language'] = lng;
    } catch { /* language not resolved yet — the API falls back to English */ }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle 401s (Unauthorized) globally.
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    const method = (error.config?.method || 'get').toLowerCase();
    // Auth endpoints handle their own errors (wrong password, rate-limit, etc.). Do NOT
    // globally redirect on those — otherwise a simple bad login or a transient error turns
    // into a full page bounce ("login error, then refresh fixes it").
    const isAuthCall = url.includes('/auth/token') || url.includes('/auth/register') || url.includes('/auth/parse');
    // Background pollers (notifications, chat/unread, nav badges, AI status/guide) run on
    // timers. A transient 401 on one of THESE must never hard-reload the page — that was
    // wiping in-progress work like the create-project form. Only real, foreground GET
    // navigations trigger a re-login.
    const isBackgroundPoll = /\/(notifications|chat|badges|unread|ai\/status|ai\/action-guide)/.test(url);
    let hadToken = false;
    try { hadToken = !!JSON.parse(localStorage.getItem('user'))?.access_token; } catch { hadToken = false; }
    // Only force a re-login when a genuinely authenticated session gets a 401 on a
    // foreground GET (never on writes/polls) — so an unexpected 401 can't destroy a form
    // the user is filling in.
    if (
      status === 401 && !isAuthCall && !isBackgroundPoll && hadToken &&
      method === 'get' && !window.location.pathname.startsWith('/login')
    ) {
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Shrink image uploads and report their progress — every authenticated upload in the app goes
// through this client, so this is the one place it has to be true.
attachUploadHandling(api);

export default api;
